/**
 * Patient medical-records client (hồ sơ bệnh án) — the four longitudinal record
 * types added on top of allergies/conditions/alerts: phẫu thuật, tiền sử gia
 * đình, kết quả xét nghiệm, ghi chú. All tenant-scoped + PHI-audited server-side.
 */
import { apiClient } from "./client";

export interface Surgery {
  id: string;
  procedureName: string;
  surgeryDate: string | null;
  hospital: string | null;
  surgeon: string | null;
  notes: string | null;
  createdAt: string;
}
export interface SurgeryInput {
  procedureName: string;
  surgeryDate?: string | null;
  hospital?: string | null;
  surgeon?: string | null;
  notes?: string | null;
}

export const FAMILY_RELATIONSHIPS: { value: string; label: string }[] = [
  { value: "FATHER", label: "Bố" },
  { value: "MOTHER", label: "Mẹ" },
  { value: "SIBLING", label: "Anh/Chị/Em" },
  { value: "GRANDPARENT", label: "Ông/Bà" },
  { value: "CHILD", label: "Con" },
  { value: "OTHER", label: "Khác" },
];
export function relationshipLabel(v: string): string {
  return FAMILY_RELATIONSHIPS.find((r) => r.value === v)?.label ?? v;
}

export interface FamilyHistory {
  id: string;
  relationship: string;
  condition: string;
  ageAtOnset: number | null;
  deceased: boolean;
  notes: string | null;
  createdAt: string;
}
export interface FamilyHistoryInput {
  relationship: string;
  condition: string;
  ageAtOnset?: number | null;
  deceased: boolean;
  notes?: string | null;
}

export const ABNORMAL_FLAGS: { value: string; label: string; tone: string }[] =
  [
    { value: "NORMAL", label: "Bình thường", tone: "success" },
    { value: "LOW", label: "Thấp", tone: "warning" },
    { value: "HIGH", label: "Cao", tone: "warning" },
    { value: "CRITICAL", label: "Nguy hiểm", tone: "danger" },
  ];
export function flagLabel(v: string | null): string {
  if (!v) return "—";
  return ABNORMAL_FLAGS.find((f) => f.value === v)?.label ?? v;
}
export function flagTone(v: string | null): string {
  return ABNORMAL_FLAGS.find((f) => f.value === v)?.tone ?? "neutral";
}

export interface LabResult {
  id: string;
  encounterId: string | null;
  testName: string;
  resultValue: string | null;
  unit: string | null;
  referenceRange: string | null;
  abnormalFlag: string | null;
  takenAt: string | null;
  labName: string | null;
  notes: string | null;
  createdAt: string;
  /** Danh mục nhạy cảm — không tự mở cho bệnh nhân (#19137). */
  sensitive: boolean;
  /** Quyết định của nhân viên: true mở, false ẩn; vắng mặt/null = theo quy tắc. */
  patientVisible?: boolean | null;
  /** Bệnh nhân có đang thấy kết quả này trên Mini App / website không (đã áp quy tắc). */
  visibleToPatient: boolean;
}
export interface LabResultInput {
  testName: string;
  resultValue?: string | null;
  unit?: string | null;
  referenceRange?: string | null;
  abnormalFlag?: string | null;
  takenAt?: string | null;
  labName?: string | null;
  notes?: string | null;
  encounterId?: string | null;
  sensitive?: boolean;
}

export const NOTE_TYPES: { value: string; label: string }[] = [
  { value: "GENERAL", label: "Chung" },
  { value: "CLINICAL", label: "Lâm sàng" },
  { value: "ADMIN", label: "Hành chính" },
  { value: "WARNING", label: "Cảnh báo" },
];
export function noteTypeLabel(v: string): string {
  return NOTE_TYPES.find((n) => n.value === v)?.label ?? v;
}

export interface PatientNote {
  id: string;
  noteType: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}
export interface PatientNoteInput {
  noteType: string;
  body: string;
}

/** Generic CRUD factory — every record type has the same 4 endpoints. */
function crud<T, I>(segment: string) {
  return {
    list(patientId: string): Promise<T[]> {
      return apiClient
        .get<T[]>(`/v1/patients/${patientId}/${segment}`)
        .then((r) => r.data);
    },
    create(patientId: string, body: I): Promise<void> {
      return apiClient
        .post(`/v1/patients/${patientId}/${segment}`, body)
        .then(() => undefined);
    },
    update(patientId: string, id: string, body: I): Promise<void> {
      return apiClient
        .put(`/v1/patients/${patientId}/${segment}/${id}`, body)
        .then(() => undefined);
    },
    remove(patientId: string, id: string): Promise<void> {
      return apiClient
        .delete(`/v1/patients/${patientId}/${segment}/${id}`)
        .then(() => undefined);
    },
  };
}

export const medicalRecordsApi = {
  surgeries: crud<Surgery, SurgeryInput>("surgeries"),
  familyHistory: crud<FamilyHistory, FamilyHistoryInput>("family-history"),
  labResults: {
    ...crud<LabResult, LabResultInput>("lab-results"),
    /** Cho bệnh nhân xem: true mở, false ẩn, null theo quy tắc (#19137). */
    setPatientVisibility(
      patientId: string,
      id: string,
      visible: boolean | null,
    ): Promise<LabResult> {
      return apiClient
        .put<LabResult>(
          `/v1/patients/${patientId}/lab-results/${id}/patient-visibility`,
          { visible },
        )
        .then((r) => r.data);
    },
  },
  notes: crud<PatientNote, PatientNoteInput>("notes"),
};

export const medicalRecordsQueryKeys = {
  surgeries: (pid: string) => ["mr", "surgeries", pid] as const,
  familyHistory: (pid: string) => ["mr", "family-history", pid] as const,
  labResults: (pid: string) => ["mr", "lab-results", pid] as const,
  notes: (pid: string) => ["mr", "notes", pid] as const,
};
